import { useQuery } from "@tanstack/react-query";
import { Breakpoint, useMediaQuery } from "@stubhub/broadway-providers";
import { Feature } from "@viagogo/feature";
import { Content } from "src/shared/components/content";
import { FeatureSetEnum } from "src/shared/constants/FeatureSetEnum";
import { useCreateQueryFn } from "src/shared/hooks/useCreateQueryFn";
import { createQueryKey } from "src/shared/utils/query-helpers";
import { QueryKeyPrefix } from "src/shared/utils/query/query-key-prefixes";
import { STALE_TIME_ONE_HOUR } from "src/shared/utils/query/retry-constants";
import { CarouselWidthController } from "src/website-apps/responsive/stubhub-home/components/CarouselWidthController";
import { SECTION_HEADER_CONTENT_ID } from "src/website-apps/responsive/stubhub-home/components/top-subcategories/constants";
import { EmptyCarousel } from "src/website-apps/shared/components/carousel";
import { useCarouselDimensions } from "src/website-apps/shared/hooks/useCarouselDimensions";
import { TopSubcategoriesList } from "./components/TopSubcategoriesList";
import { TopSubcategoriesContainer, TopSubcategoriesHeading } from "./styled-components";

const TopSubcategoriesOrEmpty = ({ dates, userLocation, usePlaceholder, size }) => {
  const createQueryFn = useCreateQueryFn();
  const { lt, lg } = userLocation || {};
  const { from, to } = dates?.dates || {};

  /**
   * Retrieves top subCategories given latest date or location filter
   */
  const { data: subcategories = [], isLoading: loading } = useQuery({
    queryKey: createQueryKey(QueryKeyPrefix.TOP_SUBCATEGORIES, { lt, lg, from, to }),
    queryFn: createQueryFn({
      method: "GetTopSubcategoryLinks",
      params: [
        ["latitude", lt],
        ["longitude", lg],
        ...(from ? [["fromDate", from.toJSON()]] : []),
        ...(to ? [["toDate", to.toJSON()]] : []),
      ],
    }),
    enabled: Boolean(lt && lg),
    staleTime: STALE_TIME_ONE_HOUR,
  });

  const isDesktop = useMediaQuery(Breakpoint.Desktop);
  const isTabletUp = useMediaQuery(Breakpoint.Tablet);
  const isTablet = isTabletUp && !isDesktop;
  const isMobile = !isTabletUp;
  const noDataAvailable = subcategories === null || subcategories.length === 0;

  const carouselDim = useCarouselDimensions();
  const emptyCarouselColumnCount = isMobile ? carouselDim.emptyCarouselColumnCount : isTablet ? 3 : 4;

  // When the placeholder is disabled, show nothing while loading and when no data is provided
  // When using a placeholder, only use it while loading and show the title too. If there is no data,
  // do not show placeholder or title, so as to match previous behavior more closely
  if (noDataAvailable && (!usePlaceholder || !loading)) {
    return null;
  }

  return (
    <TopSubcategoriesContainer>
      <TopSubcategoriesHeading>
        <Content id={SECTION_HEADER_CONTENT_ID} />
      </TopSubcategoriesHeading>
      {/* TMHomeTopCategoryRedesign (17696) deployed variant — always use CarouselWidthController */}
      <CarouselWidthController>
        {noDataAvailable ? (
          <EmptyCarousel noResult={!loading} size={emptyCarouselColumnCount} placeHolderLineCount={1} />
        ) : isMobile ? (
          <TopSubcategoriesList subcategories={subcategories} />
        ) : (
          <TopSubcategoriesList subcategories={subcategories} size={size} />
        )}
      </CarouselWidthController>
    </TopSubcategoriesContainer>
  );
};

/**
 * TopSubcategories Experiment
 * https://www.figma.com/file/Y9JIAqbF7ljecUFj4yAypS/General-Innovation-XPs---Master?node-id=13-2285&t=oEuhUVItYwY8Jqlj-4
 */
export const TopSubcategories = ({ dates, userLocation, size }) => (
  <Feature
    id={FeatureSetEnum.TMHomePlaceholderWhileLoadingPopularCategories}
    control={() => (
      <TopSubcategoriesOrEmpty dates={dates} userLocation={userLocation} usePlaceholder={false} size={size} />
    )}
    variant={() => <TopSubcategoriesOrEmpty dates={dates} userLocation={userLocation} usePlaceholder size={size} />}
  />
);
